/** Client-side JWT payload fields used for session expiry (not verified). */
export type DecodedJwtPayload = {
  sub?: string;
  role?: string;
  exp?: number;
  iat?: number;
};

const JWT_PARTS = 3;

/** Decode a JWT payload without signature verification (client expiry hints only). */
export function decodeJwtPayload(token: string): DecodedJwtPayload | null {
  const parts = token.trim().split(".");
  if (parts.length !== JWT_PARTS) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json) as DecodedJwtPayload;
  } catch {
    return null;
  }
}

/** Seconds until JWT `exp`, or null when unknown. */
export function getJwtExpiresInSeconds(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  return Math.max(0, payload.exp - Math.floor(Date.now() / 1000));
}

/** True when the access JWT is missing `exp` or within `skewSeconds` of expiry. */
export function isAccessTokenExpired(
  token: string,
  skewSeconds = 60,
): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + skewSeconds;
}
